import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress } from '@mui/material';
import { Button } from 'src/UIKit/components';
import { useShortLinkApi } from 'src/modules/shortLinks/api/ShortLinksApi';

export const ShortUrlResolver: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { resolveShortUrl } = useShortLinkApi();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const resolveUrl = async () => {
      if (!code) {
        setError('Invalid short URL code');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const response = await resolveShortUrl(code);
        const linkData = response.data;

        if (linkData && linkData.url) {
          // Redirect to the resolved URL
          window.location.href = linkData.url;
        } else {
          setError('Invalid short URL');
          setIsLoading(false);
        }
      } catch (error: unknown) {
        console.error('Error resolving short URL:', error);

        // Handle different error cases
        const axiosError = error as { response?: { status?: number } };
        if (axiosError?.response?.status === 404) {
          setError('Short URL not found');
        } else if (axiosError?.response?.status === 410) {
          setError('This short URL has expired');
        } else {
          setError('Unable to resolve short URL');
        }
        setIsLoading(false);
      }
    };

    resolveUrl();
  }, [code, navigate]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: 3,
        }}
      >
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          Resolving short URL...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: 3,
        textAlign: 'center',
      }}
    >
      <Typography variant="h4" color="error" gutterBottom>
        404 - Page Not Found
      </Typography>
      <Typography variant="h6" color="text.secondary" gutterBottom>
        {error || 'The requested page could not be found'}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        The short URL you&apos;re looking for doesn&apos;t exist or has expired.
      </Typography>
      <Button variant="text" onClick={() => navigate('/')}>
        Return to Home
      </Button>
    </Box>
  );
};
